// src/types/types.ts - Updated with new API response format

// Represents a team in the tournament
export interface Team {
  id: string;
  name: string;
  seed: number;
  region: string;
}

// Regions in the tournament
export enum Region {
  WEST = "West",
  EAST = "East",
  SOUTH = "South",
  MIDWEST = "Midwest"
}

// Winner source type
export enum WinnerSource {
  OFFICIAL = "official",
  USER = "user",
  SCENARIO = "scenario"
}

// Winner with metadata
export interface WinnerWithMetadata {
  team: string;
  source: WinnerSource;
}

// Player pick data structure
export interface PlayerPicks {
  name: string;
  picks: string[];
}

// Request payload for simulation
export interface SimulationRequest {
  playerPicks?: string;      // Raw text input of player picks (omit when poolId is set)
  poolId?: string;           // Pool name — server loads picks from storage
  knownWinners: string[];    // Teams that have already won
  sampleSize: number;        // Number of scenarios to simulate
  roundNames?: string[];     // Optional round names configuration
  pointsPerRound?: number[]; // Optional points per round configuration
}

// Response from simulation
export interface SimulationResult {
  players: PlayerResult[];
  totalScenarios: number;
  totalPossibleScenarios: number;
  tiesPossible: number;
  tiesPercentage: number;
  sampled: boolean;
}

// Individual player result - updated for new API format
export interface PlayerResult {
  name: string;
  wins: number;                    // Number of winning paths (used for "Paths" column)
  percentage: number;              // Legacy field (may still be present)
  probabilityPercentage?: number;  // New field - probability percentage (used for "Probability" column)
  winPercentage?: number;          // New field from API (not used in display)
  eliminationStatus?: 'ALIVE' | 'ELIMINATED'; // Elimination status
  currentScore?: number;           // Optional current score based on known winners
}

// Game in rooting interests
export interface GameInterest {
  gameName: string;
  preferredTeam: string;
  impact: number;
  winPercentWithPreferred: number;
  winPercentWithoutPreferred: number;
  isMustWin: boolean;
  round: number;
}

// Rooting interests response
export interface RootingInterests {
  games: GameInterest[];
  noImpactGames: string[];
}

// Bracket game representation
export interface BracketGame {
  id: number;
  round: number;
  region: Region | "Final Four" | "Championship";
  teamA?: string;
  teamB?: string;
  winner?: string;
  nextGameId?: number;
}

// Shared scenario data from the public API
export interface SharedScenarioData {
  scenarioName: string;
  poolName: string;
  knownWinners: string[];
  playerPicks: string;
  pointValues: number[];
  createdAt: string;
}

// Chances over time snapshot
export interface ChancesSnapshot {
  gameIndex: number;
  bracketSlot: number | null;
  roundName: string | null;
  advancesTo: string | null;
  winner: string | null;
  loser: string | null;
  winnerSeed: number | null;
  loserSeed: number | null;
  sampleSize: number;
  playerResults: { name: string; probabilityPercentage: number }[];
}

// Response from chances-over-time endpoint
export interface ChancesOverTimeResponse {
  snapshots: ChancesSnapshot[];
  totalSnapshots: number;
  snapshotsExpected: number;
  computing: boolean;
  computeTimeSeconds: number;
}

// Loading state type
export type LoadingState = "idle" | "loading" | "success" | "error";